import { del, get, set } from 'idb-keyval';

import { SYNC_JOB_STORAGE_KEY, SyncJob } from './types';

export type OfflineSyncDoneRecord = {
  at: number;
  plots: number;
  analyses: number;
  includeHeatmaps: boolean;
};

export async function getSyncJob(): Promise<SyncJob | undefined> {
  return get<SyncJob>(SYNC_JOB_STORAGE_KEY);
}

export async function saveSyncJob(job: SyncJob): Promise<void> {
  await set(SYNC_JOB_STORAGE_KEY, { ...job, updatedAt: Date.now() });
}

export async function clearSyncJob(): Promise<void> {
  await del(SYNC_JOB_STORAGE_KEY);
}

export function offlineSyncDoneKey(
  campaignId: number,
  managerId?: number
): string {
  return `offline_sync_done:${campaignId}:${managerId ?? 'all'}`;
}

export function parseOfflineSyncDone(
  raw: string | null
): OfflineSyncDoneRecord | null {
  if (!raw) {
    return null;
  }
  if (raw === 'true') {
    return {
      at: 0,
      plots: 0,
      analyses: 0,
      includeHeatmaps: false,
    };
  }
  try {
    const parsed = JSON.parse(raw) as OfflineSyncDoneRecord;
    if (typeof parsed.at !== 'number') {
      return null;
    }
    return {
      at: parsed.at,
      plots: parsed.plots ?? 0,
      analyses: parsed.analyses ?? 0,
      includeHeatmaps: !!parsed.includeHeatmaps,
    };
  } catch {
    return null;
  }
}

export function getOfflineSyncDoneRecord(
  campaignId: number,
  managerId?: number
): OfflineSyncDoneRecord | null {
  return parseOfflineSyncDone(
    window.localStorage.getItem(offlineSyncDoneKey(campaignId, managerId))
  );
}

export function isOfflineSyncDone(
  campaignId: number,
  managerId?: number
): boolean {
  return getOfflineSyncDoneRecord(campaignId, managerId) != null;
}

export type MarkOfflineSyncDoneMeta = {
  plots: number;
  analyses: number;
  includeHeatmaps: boolean;
};

export function markOfflineSyncDone(
  campaignId: number,
  managerId: number | undefined,
  meta: MarkOfflineSyncDoneMeta
): void {
  const record: OfflineSyncDoneRecord = {
    at: Date.now(),
    plots: meta.plots,
    analyses: meta.analyses,
    includeHeatmaps: meta.includeHeatmaps,
  };
  window.localStorage.setItem(
    offlineSyncDoneKey(campaignId, managerId),
    JSON.stringify(record)
  );
}
